import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Settings, 
  Save, 
  AlertCircle, 
  CheckCircle, 
  Eye, 
  EyeOff,
  Upload,
  Trash2,
  Plus,
  Building2,
  Smartphone,
  Mail,
  MapPin,
  FileText,
  Truck
} from 'lucide-react';
import { paymentMethodsAPI } from '../../services/api';

const PaymentMethodsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('cashfree');
  const [showCashfreeSecret, setShowCashfreeSecret] = useState(false);
  const [showRazorpayTestSecret, setShowRazorpayTestSecret] = useState(false);
  const [showRazorpayLiveSecret, setShowRazorpayLiveSecret] = useState(false);
  const [showPhonepeSaltKey, setShowPhonepeSaltKey] = useState(false);
  
  const [cashfreeConfig, setCashfreeConfig] = useState({
    app_id: '',
    secret_key: '',
    is_active: false,
    test_mode: true
  });

  const [offlineConfig, setOfflineConfig] = useState({
    is_active: false,
    qr_code_image: '',
    bank_details: {
      account_name: '',
      account_number: '',
      ifsc_code: '',
      bank_name: '',
      branch: ''
    },
    upi_details: {
      upi_id: '',
      upi_name: ''
    },
    payment_instructions: '',
    contact_info: {
      phone: '',
      email: '',
      address: ''
    }
  });

  const [codConfig, setCodConfig] = useState({
    is_active: true
  });

  const [razorpayConfig, setRazorpayConfig] = useState({
    test_api_key: '',
    test_api_secret: '',
    live_api_key: '',
    live_api_secret: '',
    webhook_secret: '',
    is_active: false,
    test_mode: true
  });

  const [phonepeConfig, setPhonepeConfig] = useState({
    merchant_id: '',
    salt_key: '',
    salt_index: 1,
    is_active: false,
    test_mode: true
  });

  const tabs = [
    { id: 'cashfree', label: 'Cashfree Payment', icon: CreditCard },
    { id: 'razorpay', label: 'Razorpay Payment', icon: CreditCard },
    { id: 'phonepe', label: 'PhonePe Payment', icon: Smartphone },
    { id: 'offline', label: 'Offline Payment', icon: Building2 },
    { id: 'cod', label: 'Cash on Delivery', icon: Truck }
  ];

  // Fetch payment methods configuration
  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await paymentMethodsAPI.getConfig();
      
      if (response?.data?.success) {
        const data = response.data.data;
        
        if (data.cashfree) {
          setCashfreeConfig(data.cashfree);
        }
        
        if (data.offline) {
          // Merge with default structure to ensure all nested objects exist
          const defaultOfflineConfig = {
            is_active: false,
            qr_code_image: '',
            bank_details: {
              account_name: '',
              account_number: '',
              ifsc_code: '',
              bank_name: '',
              branch: ''
            },
            upi_details: {
              upi_id: '',
              upi_name: ''
            },
            payment_instructions: '',
            contact_info: {
              phone: '',
              email: '',
              address: ''
            }
          };
          
          const mergedConfig = {
            ...defaultOfflineConfig,
            ...data.offline,
            bank_details: {
              ...defaultOfflineConfig.bank_details,
              ...(data.offline?.bank_details || {})
            },
            upi_details: {
              ...defaultOfflineConfig.upi_details,
              ...(data.offline?.upi_details || {})
            },
            contact_info: {
              ...defaultOfflineConfig.contact_info,
              ...(data.offline?.contact_info || {})
            }
          };
          
          setOfflineConfig(mergedConfig);
        }
        
        if (data.cod) {
          setCodConfig(data.cod);
        }
        
        if (data.razorpay) {
          setRazorpayConfig(data.razorpay);
        }
        
        if (data.phonepe) {
          setPhonepeConfig(data.phonepe);
        }
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setError('Failed to load payment methods configuration');
    } finally {
      setLoading(false);
    }
  };

  // Save Cashfree configuration
  const saveCashfreeConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await paymentMethodsAPI.updateCashfreeConfig(cashfreeConfig);
      
      if (response?.data?.success) {
        setSuccess('Cashfree configuration saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save Cashfree configuration');
      }
    } catch (err) {
      console.error('Error saving Cashfree config:', err);
      setError(err.response?.data?.message || 'Failed to save Cashfree configuration');
    } finally {
      setSaving(false);
    }
  };

  // Save Offline configuration
  const saveOfflineConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await paymentMethodsAPI.updateOfflineConfig(offlineConfig);
      
      if (response?.data?.success) {
        setSuccess('Offline payment configuration saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save offline payment configuration');
      }
    } catch (err) {
      console.error('Error saving offline config:', err);
      setError(err.response?.data?.message || 'Failed to save offline payment configuration');
    } finally {
      setSaving(false);
    }
  };

  // Save COD configuration
  const saveCodConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await paymentMethodsAPI.updateCodConfig(codConfig);
      
      if (response?.data?.success) {
        setSuccess('Cash on Delivery configuration saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save Cash on Delivery configuration');
      }
    } catch (err) {
      console.error('Error saving COD config:', err);
      setError(err.response?.data?.message || 'Failed to save Cash on Delivery configuration');
    } finally {
      setSaving(false);
    }
  };

  // Save Razorpay configuration
  const saveRazorpayConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await paymentMethodsAPI.updateRazorpayConfig(razorpayConfig);
      
      if (response?.data?.success) {
        setSuccess('Razorpay configuration saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save Razorpay configuration');
      }
    } catch (err) {
      console.error('Error saving Razorpay config:', err);
      setError(err.response?.data?.message || 'Failed to save Razorpay configuration');
    } finally {
      setSaving(false);
    }
  };

  // Save PhonePe configuration
  const savePhonepeConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await paymentMethodsAPI.updatePhonepeConfig(phonepeConfig);
      
      if (response?.data?.success) {
        setSuccess('PhonePe configuration saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to save PhonePe configuration');
      }
    } catch (err) {
      console.error('Error saving PhonePe config:', err);
      setError(err.response?.data?.message || 'Failed to save PhonePe configuration');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const handleCashfreeChange = (field, value) => {
    setCashfreeConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRazorpayChange = (field, value) => {
    setRazorpayConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhonepeChange = (field, value) => {
    setPhonepeConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOfflineChange = (section, field, value) => {
    setOfflineConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: field === 'is_active' ? Boolean(value) : value
      }
    }));
  };

  const handleCodChange = (field, value) => {
    setCodConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOfflineNestedChange = (section, subsection, field, value) => {
    // Ensure value is a string and not undefined
    const stringValue = String(value || '');
    
    setOfflineConfig(prev => {
      const newConfig = {
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...(prev[section]?.[subsection] || {}),
            [field]: stringValue
          }
        }
      };
      return newConfig;
    });
  };

  // Simplified handlers for specific fields
  const handleBankDetailsChange = (field, value) => {
    setOfflineConfig(prev => ({
      ...prev,
      bank_details: {
        ...prev.bank_details,
        [field]: String(value || '')
      }
    }));
  };

  const handleUpiDetailsChange = (field, value) => {
    setOfflineConfig(prev => ({
      ...prev,
      upi_details: {
        ...prev.upi_details,
        [field]: String(value || '')
      }
    }));
  };

  const handleContactInfoChange = (field, value) => {
    setOfflineConfig(prev => ({
      ...prev,
      contact_info: {
        ...prev.contact_info,
        [field]: String(value || '')
      }
    }));
  };

  const handlePaymentInstructionsChange = (value) => {
    setOfflineConfig(prev => ({
      ...prev,
      payment_instructions: String(value || '')
    }));
  };

  const handleQrCodeChange = (value) => {
    setOfflineConfig(prev => ({
      ...prev,
      qr_code_image: String(value || '')
    }));
  };

  const handleIsActiveChange = (value) => {
    setOfflineConfig(prev => ({
      ...prev,
      is_active: Boolean(value)
    }));
  };

  const renderCashfreeTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
            Cashfree Payment Gateway
          </h3>
        </div>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Configure your Cashfree API credentials to enable online payments through Cashfree gateway.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
        {/* App ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            App ID *
          </label>
          <input
            type="text"
            value={cashfreeConfig.app_id}
            onChange={(e) => handleCashfreeChange('app_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Enter your Cashfree App ID"
          />
        </div>

        {/* Secret Key */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Secret Key *
          </label>
          <div className="relative">
            <input
              type={showCashfreeSecret ? 'text' : 'password'}
              value={cashfreeConfig.secret_key}
              onChange={(e) => handleCashfreeChange('secret_key', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Enter your Cashfree Secret Key"
            />
            <button
              type="button"
              onClick={() => setShowCashfreeSecret(!showCashfreeSecret)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showCashfreeSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Settings */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Cashfree Payments</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">Allow customers to pay using Cashfree</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={cashfreeConfig.is_active}
              onChange={(e) => handleCashfreeChange('is_active', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Test Mode</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">Use test credentials for development</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={cashfreeConfig.test_mode}
              onChange={(e) => handleCashfreeChange('test_mode', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveCashfreeConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderOfflineTab = () => (
    <div className="space-y-6">
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">
            Offline Payment Method
          </h3>
        </div>
        <p className="text-sm text-green-700 dark:text-green-300">
          Configure offline payment details like bank account information, UPI details, and payment instructions.
        </p>
      </div>

      {/* Enable/Disable */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Offline Payments</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">Allow customers to pay offline</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={offlineConfig.is_active}
            onChange={(e) => handleIsActiveChange(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
        </label>
      </div>

      {/* Bank Details */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Bank Account Details
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Account Holder Name
            </label>
            <input
              type="text"
              value={offlineConfig.bank_details.account_name}
              onChange={(e) => handleBankDetailsChange('account_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Account holder name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Account Number
            </label>
            <input
              type="text"
              value={offlineConfig.bank_details.account_number}
              onChange={(e) => handleBankDetailsChange('account_number', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Account number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              IFSC Code
            </label>
            <input
              type="text"
              value={offlineConfig.bank_details.ifsc_code}
              onChange={(e) => handleBankDetailsChange('ifsc_code', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="IFSC code"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Bank Name
            </label>
            <input
              type="text"
              value={offlineConfig.bank_details.bank_name}
              onChange={(e) => handleBankDetailsChange('bank_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Bank name"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Branch
            </label>
            <input
              type="text"
              value={offlineConfig.bank_details.branch}
              onChange={(e) => handleBankDetailsChange('branch', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Branch name"
            />
          </div>
        </div>
      </div>

      {/* UPI Details */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Smartphone className="w-5 h-5" />
          UPI Details
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              UPI ID
            </label>
            <input
              type="text"
              value={offlineConfig.upi_details.upi_id}
              onChange={(e) => handleUpiDetailsChange('upi_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="example@upi"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              UPI Name
            </label>
            <input
              type="text"
              value={offlineConfig.upi_details.upi_name}
              onChange={(e) => handleUpiDetailsChange('upi_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="UPI display name"
            />
          </div>
        </div>
      </div>

      {/* QR Code */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Upload className="w-5 h-5" />
          QR Code
        </h4>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            QR Code Image URL
          </label>
          <input
            type="text"
            value={offlineConfig.qr_code_image}
            onChange={(e) => handleQrCodeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="https://example.com/qr-code.png"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Enter the URL of your QR code image. This will be displayed to customers for easy payment scanning.
          </p>
        </div>

        {/* QR Code Preview */}
        {offlineConfig.qr_code_image && offlineConfig.qr_code_image.trim() !== '' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              QR Code Preview
            </label>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
              <img 
                src={offlineConfig.qr_code_image} 
                alt="QR Code" 
                className="max-w-xs mx-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="text-center text-sm text-gray-500 dark:text-gray-400" style={{display: 'none'}}>
                Failed to load QR code image
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Contact Information
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={offlineConfig.contact_info.phone}
              onChange={(e) => handleContactInfoChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Contact phone number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={offlineConfig.contact_info.email}
              onChange={(e) => handleContactInfoChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Contact email"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Address
            </label>
            <textarea
              value={offlineConfig.contact_info.address}
              onChange={(e) => handleContactInfoChange('address', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Company address"
            />
          </div>
        </div>
      </div>

      {/* Payment Instructions */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Payment Instructions
        </h4>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Instructions for Customers
          </label>
          <textarea
            value={offlineConfig.payment_instructions}
            onChange={(e) => handlePaymentInstructionsChange(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Enter detailed payment instructions for customers..."
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            This will be displayed to customers when they select offline payment method.
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveOfflineConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderRazorpayTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
            Razorpay Payment Gateway
          </h3>
        </div>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Configure your Razorpay API credentials to enable online payments through Razorpay gateway.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
        {/* Test API Key */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Test API Key
          </label>
          <input
            type="text"
            value={razorpayConfig.test_api_key}
            onChange={(e) => handleRazorpayChange('test_api_key', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="rzp_test_..."
          />
        </div>

        {/* Test API Secret */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Test API Secret
          </label>
          <div className="relative">
            <input
              type={showRazorpayTestSecret ? "text" : "password"}
              value={razorpayConfig.test_api_secret}
              onChange={(e) => handleRazorpayChange('test_api_secret', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Enter test API secret"
            />
            <button
              type="button"
              onClick={() => setShowRazorpayTestSecret(!showRazorpayTestSecret)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showRazorpayTestSecret ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Live API Key */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Live API Key
          </label>
          <input
            type="text"
            value={razorpayConfig.live_api_key}
            onChange={(e) => handleRazorpayChange('live_api_key', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="rzp_live_..."
          />
        </div>

        {/* Live API Secret */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Live API Secret
          </label>
          <div className="relative">
            <input
              type={showRazorpayLiveSecret ? "text" : "password"}
              value={razorpayConfig.live_api_secret}
              onChange={(e) => handleRazorpayChange('live_api_secret', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Enter live API secret"
            />
            <button
              type="button"
              onClick={() => setShowRazorpayLiveSecret(!showRazorpayLiveSecret)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showRazorpayLiveSecret ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Webhook Secret */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Webhook Secret
          </label>
          <input
            type="text"
            value={razorpayConfig.webhook_secret}
            onChange={(e) => handleRazorpayChange('webhook_secret', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="Enter webhook secret"
          />
        </div>
      </div>

      {/* Settings */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Razorpay Payments</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">Allow customers to pay using Razorpay</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={razorpayConfig.is_active}
              onChange={(e) => handleRazorpayChange('is_active', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Test Mode</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">Use test credentials for development</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={razorpayConfig.test_mode}
              onChange={(e) => handleRazorpayChange('test_mode', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveRazorpayConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderPhonepeTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Smartphone className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
            PhonePe Payment Gateway
          </h3>
        </div>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Configure your PhonePe API credentials to enable online payments through PhonePe gateway.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
        {/* Merchant ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Merchant ID
          </label>
          <input
            type="text"
            value={phonepeConfig.merchant_id}
            onChange={(e) => handlePhonepeChange('merchant_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="Enter merchant ID"
          />
        </div>

        {/* Salt Key */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Salt Key
          </label>
          <div className="relative">
            <input
              type={showPhonepeSaltKey ? "text" : "password"}
              value={phonepeConfig.salt_key}
              onChange={(e) => handlePhonepeChange('salt_key', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Enter salt key"
            />
            <button
              type="button"
              onClick={() => setShowPhonepeSaltKey(!showPhonepeSaltKey)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPhonepeSaltKey ? (
                <EyeOff className="h-4 w-4 text-gray-400" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Salt Index */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Salt Index
          </label>
          <input
            type="number"
            min="1"
            max="10"
            value={phonepeConfig.salt_index}
            onChange={(e) => handlePhonepeChange('salt_index', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="1"
          />
        </div>
      </div>

      {/* Settings */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable PhonePe Payments</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">Allow customers to pay using PhonePe</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={phonepeConfig.is_active}
              onChange={(e) => handlePhonepeChange('is_active', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Test Mode</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">Use test credentials for development</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={phonepeConfig.test_mode}
              onChange={(e) => handlePhonepeChange('test_mode', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={savePhonepeConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderCodTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Truck className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
            Cash on Delivery Payment Method
          </h3>
        </div>
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Configure Cash on Delivery payment method. When enabled, customers can pay when they receive their order.
        </p>
      </div>

      {/* Enable/Disable */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Cash on Delivery</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">Allow customers to pay when they receive their order</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={codConfig.is_active}
            onChange={(e) => handleCodChange('is_active', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
        </label>
      </div>

      {/* Information */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">How it works</h4>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Customer places order and selects Cash on Delivery</li>
          <li>• Order is processed and shipped</li>
          <li>• Customer pays when they receive the order</li>
          <li>• Payment is collected by delivery personnel</li>
        </ul>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveCodConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Payment Methods Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Configure payment gateways and offline payment methods
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
            <CheckCircle className="w-5 h-5" />
            <span>{success}</span>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 sm:p-6">
        {/* Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-3" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
            {activeTab === 'cashfree' ? renderCashfreeTab() : 
             activeTab === 'razorpay' ? renderRazorpayTab() :
             activeTab === 'phonepe' ? renderPhonepeTab() :
             activeTab === 'offline' ? renderOfflineTab() : 
             activeTab === 'cod' ? renderCodTab() : renderCashfreeTab()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsManagement; 